// Sustituto de 'firebase/storage': sube a /api/storage y sirve desde /files
import { apiCall, FirebaseError } from './wire.js';

const storageInstance = { app: null, maxUploadRetryTime: 0 };
export function getStorage() { return storageInstance; }
export function connectStorageEmulator() {}

const urls = new Map();

class StorageReference {
  constructor(path) {
    this.fullPath = String(path || '').replace(/^\/+/, '');
    this.name = this.fullPath.split('/').pop() || '';
    this.bucket = 'local';
    this.storage = storageInstance;
  }
  get parent() {
    const segs = this.fullPath.split('/');
    return segs.length > 1 ? new StorageReference(segs.slice(0, -1).join('/')) : null;
  }
  get root() { return new StorageReference(''); }
  child(p) { return new StorageReference(`${this.fullPath}/${p}`.replace(/\/+/g, '/')); }
  toString() { return `local://${this.fullPath}`; }
}

export function ref(storageOrRef, path) {
  if (storageOrRef instanceof StorageReference) return storageOrRef.child(path || '');
  if (path && (/^https?:\/\//.test(path) || path.startsWith('/files/'))) {
    // URL de un archivo propio: /files/<ruta>?token=...
    const m = new URL(path, location.origin).pathname.match(/^\/files\/(.+)$/);
    return new StorageReference(m ? decodeURIComponent(m[1]) : path);
  }
  return new StorageReference(path || '');
}

async function upload(r, body, contentType) {
  const qs = new URLSearchParams({ path: r.fullPath, contentType });
  const res = await apiCall(`/storage/upload?${qs}`, null, { raw: body, headers: { 'Content-Type': contentType } });
  urls.set(r.fullPath, res.url);
  const metadata = {
    fullPath: res.path, name: r.name, bucket: 'local', size: res.size,
    contentType, timeCreated: new Date().toISOString(),
  };
  return { ref: r, metadata };
}

export async function uploadBytes(r, data, metadata = {}) {
  const contentType = metadata.contentType || data?.type || 'application/octet-stream';
  return upload(r, data, contentType);
}

export async function uploadString(r, value, format = 'raw', metadata = {}) {
  let blob;
  if (format === 'data_url') {
    blob = await (await fetch(value)).blob();
  } else if (format === 'base64' || format === 'base64url') {
    const b64 = format === 'base64url' ? value.replace(/-/g, '+').replace(/_/g, '/') : value;
    const bin = atob(b64);
    const arr = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
    blob = new Blob([arr], { type: metadata.contentType || 'application/octet-stream' });
  } else {
    blob = new Blob([value], { type: metadata.contentType || 'text/plain' });
  }
  return upload(r, blob, metadata.contentType || blob.type || 'application/octet-stream');
}

export function uploadBytesResumable(r, data, metadata = {}) {
  const p = uploadBytes(r, data, metadata);
  const size = data?.size || 0;
  const task = {
    snapshot: { bytesTransferred: 0, totalBytes: size, state: 'running', ref: r },
    on(evt, next, error, complete) {
      if (next && typeof next === 'object') ({ next, error, complete } = next);
      p.then(() => {
        task.snapshot = { bytesTransferred: size, totalBytes: size, state: 'success', ref: r };
        next?.(task.snapshot);
        complete?.();
      }, (e) => error?.(e));
      return () => {};
    },
    then: (a, b) => p.then(() => task.snapshot).then(a, b),
    catch: (b) => p.catch(b),
    pause: () => false,
    resume: () => false,
    cancel: () => false,
  };
  return task;
}

export async function getDownloadURL(r) {
  if (urls.has(r.fullPath)) return urls.get(r.fullPath);
  const res = await apiCall('/storage/url', { path: r.fullPath });
  urls.set(r.fullPath, res.url);
  return res.url;
}

export async function deleteObject(r) {
  await apiCall('/storage/delete', { path: r.fullPath });
  urls.delete(r.fullPath);
}

export async function getMetadata(r) { return { fullPath: r.fullPath, name: r.name, bucket: 'local' }; }
export async function listAll() { throw new FirebaseError('storage/unimplemented', 'listAll no está disponible'); }
export { StorageReference };
